// Company overview edition, shared by the "Informations légales" modal.
// These rules used to live inline in the tab; they are pure, and the billing
// data they guard (SIRET, code APE) makes them worth testing on their own.

// The endpoint patches only what it receives, so the draft is compared field by
// field. Order matters only for readability.
const FIELDS_TO_COMPARE = [
  { path: 'reason_social', normalizeAsString: true },
  { path: 'name_commercial', normalizeAsString: true },
  { path: 'address.city', normalizeAsString: true },
  { path: 'address.postcode', normalizeAsString: true },
  { path: 'address.street', normalizeAsString: true },
  { path: 'siret', normalizeAsString: true },
  { path: 'status', normalizeAsString: true },
  { path: 'code_ape', normalizeAsString: true },
  // staff arrives as a number and is typed back as a string: comparing it as a
  // string keeps an untouched field from looking dirty on every open.
  { path: 'staff', normalizeAsString: true },
  { path: 'conv_collective', normalizeAsString: true },
  { path: 'companySupplier', normalizeAsString: true },
];

// companySupplier is an object once read back from the API and an id while
// being edited, so it is always compared through its id.
const getValue = (source, path) => {
  if (path === 'companySupplier') {
    if (!source?.companySupplier) return '';
    if (typeof source.companySupplier === 'object') {
      return source.companySupplier._id?.toString() || '';
    }
    return String(source.companySupplier);
  }

  return path
    .split('.')
    .reduce(
      (accumulator, key) => (accumulator ? accumulator[key] : undefined),
      source,
    );
};

const normalizeValue = (value, asString) => {
  if (value === undefined || value === null) return '';
  return asString ? value.toString() : value;
};

export const diffCompanyOverview = (draft, original) => {
  const modifiedFields = {};
  let addressModified = false;

  FIELDS_TO_COMPARE.forEach(({ path, normalizeAsString }) => {
    const modified = normalizeValue(getValue(draft, path), normalizeAsString);
    const initial = normalizeValue(getValue(original, path), normalizeAsString);
    if (modified === initial) return;

    if (path.startsWith('address.')) {
      addressModified = true;
    } else if (path === 'companySupplier') {
      modifiedFields.companySupplier = getValue(draft, path) || null;
    } else {
      modifiedFields[path] = getValue(draft, path);
    }
  });

  // The address is stored as a sub-document the endpoint replaces wholesale:
  // sending a lone city would drop the street and the postcode.
  if (addressModified) modifiedFields.address = draft.address;

  return modifiedFields;
};

export const hasCompanyOverviewChanges = (draft, original) =>
  Object.keys(diffCompanyOverview(draft, original)).length > 0;

// An empty field is not an error: a company can be created before these are
// known, and blocking on emptiness would make it uneditable.
export const validateCompanyOverview = (draft) => ({
  errorSiret:
    draft?.siret && !/^\d{14}$/.test(draft.siret)
      ? 'Le siret doit être composé de 14 chiffres.'
      : '',
  errorCodeApe:
    draft?.code_ape && !/^\d{4}[A-Za-z]$/.test(draft.code_ape)
      ? "Le code APE doit être composé de 4 chiffres suivis d'une lettre."
      : '',
  errorStaff:
    draft?.staff && !/^\d+$/.test(String(draft.staff))
      ? 'Le staff doit être composé uniquement de chiffres.'
      : '',
});

export const isCompanyOverviewValid = (errors) =>
  !errors.errorSiret && !errors.errorCodeApe && !errors.errorStaff;

// Pennylane sends the category id as a number, the select hands it back as a
// string, and "no category" is expressed as null, '' or undefined depending on
// where it comes from. Comparing raw values would flag a change that is not one.
export const normalizePennylaneCategoryId = (id) =>
  id == null || id === '' ? null : Number(id);
